import React, { useEffect, useState } from 'react';
import ModelFlow from './components/ModelFlow';
import AdminApp from './admin/AdminApp';
import Landing from './Landing';
import { api } from './api';
import { LangProvider } from './i18n';
import type { ReleaseRecord, ServerConfig } from '@shared/types';

type Route =
  | { kind: 'sign'; token: string }
  | { kind: 'admin' }
  | { kind: 'landing' };

const parseRoute = (): Route => {
  const path = window.location.pathname;
  const signMatch = path.match(/^\/sign\/([A-Za-z0-9_-]+)\/?$/);
  if (signMatch) return { kind: 'sign', token: signMatch[1] };
  if (path === '/admin' || path.startsWith('/admin/')) return { kind: 'admin' };
  return { kind: 'landing' };
};

const App: React.FC = () => {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [release, setRelease] = useState<ReleaseRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const cfg = await api.config();
        if (cancelled) return;
        setConfig(cfg);
        if (route.kind === 'sign') {
          const rec = await api.getRelease(route.token);
          if (!cancelled) setRelease(rec);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  if (route.kind === 'sign') {
    return (
      <LangProvider fixed="en">
        {loadError || !release ? (
          <SignLinkNotFound />
        ) : (
          <div className="min-h-screen bg-gray-50">
            <ModelFlow release={release} token={route.token} />
          </div>
        )}
      </LangProvider>
    );
  }

  if (route.kind === 'admin') {
    if (!config) return null;
    return (
      <LangProvider fixed="ru">
        <AdminApp config={config} />
      </LangProvider>
    );
  }

  return (
    <LangProvider fixed="en">
      <Landing />
    </LangProvider>
  );
};

const SignLinkNotFound: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
    <div className="max-w-md text-center">
      <h1 className="text-xl font-bold serif mb-2">Link not found</h1>
      <p className="text-gray-500 text-sm">
        This signing link is invalid or has expired. Ask the photographer for a new one.
      </p>
    </div>
  </div>
);

export default App;
